import { Component, ChangeDetectionStrategy, inject, signal, computed, afterNextRender, Injector, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule, CardModule, AlertModule, SpinnerModule, BadgeModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { TranslationModule } from 'i18n';
import { CurrencyAmountPipe, Logger } from 'shared';
import { OrderService } from '../../../../../../services/order.service';
import { loadScript, PayPalNamespace } from '@paypal/paypal-js';
import { lastValueFrom } from 'rxjs';

/**
 * PayPal (Orders v2) confirmation step, rendered as PayPal Smart Buttons.
 *
 * Generic and gateway-agnostic in the same way as StripeConfirmationComponent:
 * the backend creates the PayPal order (initiate_payment) and captures it
 * (confirm_paypal); this component only hosts the buttons. It is reused across
 * frontends — each frontend supplies its own PayPal account via backend env
 * vars, so nothing here is app-specific.
 */
/** Why a PayPal flow ended without a payment (see PaypalConfirmationComponent.onFailed). */
export interface PaypalFailure {
  reason: 'cancelled' | 'error' | 'declined';
  message?: string;
}

@Component({
  selector: 'app-paypal-confirmation',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    AlertModule,
    SpinnerModule,
    BadgeModule,
    IconModule,
    TranslationModule,
    CurrencyAmountPipe
  ],
  templateUrl: './paypal-confirmation.component.html',
  styleUrls: ['./paypal-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaypalConfirmationComponent {
  @Input() orderId: number | null = null;
  @Input() totalPrice: number = 0;
  @Input() paymentCosts: number = 0;
  @Input() totalWithCosts: number = 0;
  @Input() currency: string = 'USD';
  @Output() onSuccess = new EventEmitter<any>();
  @Output() onBack = new EventEmitter<void>();
  /**
   * Optional: the PayPal flow ended without a payment — the payer closed or
   * cancelled the PayPal window ('cancelled'), PayPal reported an error ('error'),
   * or PayPal refused the capture, e.g. a declined card ('declined'). A host that
   * binds this can keep the order and offer other methods; a host that does not
   * keeps the previous behaviour exactly (inline message, retry in place).
   */
  @Output() onFailed = new EventEmitter<PaypalFailure>();

  protected logger = new Logger('PaypalConfirmationComponent');
  private orderService = inject(OrderService);

  // ── Reactive state ──────────────────────────────────────────────
  initializing = signal<boolean>(true);
  processing = signal<boolean>(false);
  errorMessage = signal<string>('');
  paypalOrderId = signal<string>('');
  clientId = signal<string>('');
  paypalMode = signal<string>('');

  private paypal: PayPalNamespace | null = null;
  private buttonsRendered = false;
  private injector = inject(Injector);

  formattedTotal = computed(() =>
    this.totalWithCosts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );

  constructor() {
    afterNextRender(async () => {
      if (!this.orderId) {
        this.errorMessage.set('Order ID is missing');
        this.initializing.set(false);
        return;
      }
      await this.initializePaypalPayment();
    });
  }

  private async initializePaypalPayment(): Promise<void> {
    this.errorMessage.set('');
    this.initializing.set(true);

    try {
      const response = await lastValueFrom(this.orderService.initiatePayment(this.orderId!, 'paypal'));

      this.paypalOrderId.set(response.paypal_order_id || '');
      this.clientId.set(response.paypal_client_id || '');
      this.paypalMode.set(response.paypal_mode || 'sandbox');
      if (response.paypal_currency) this.currency = response.paypal_currency;

      if (!this.paypalOrderId() || !this.clientId()) {
        throw new Error('Invalid PayPal configuration from server');
      }

      this.paypal = await loadScript({
        clientId: this.clientId(),
        currency: this.currency,
        intent: 'capture',
        components: 'buttons'
      });

      if (!this.paypal || !this.paypal.Buttons) {
        throw new Error('Failed to load PayPal');
      }

      this.initializing.set(false);

      // Mount buttons once the container is in the DOM. Pass the injector so
      // this runs correctly even though we're outside the initial injection
      // context (we're in an async continuation).
      afterNextRender(() => this.renderButtons(), { injector: this.injector });
    } catch (err: any) {
      this.logger.error('PayPal init error:', err);
      this.errorMessage.set(err?.error?.error || err?.message || 'Failed to initialize PayPal. Please try again.');
      this.initializing.set(false);
    }
  }

  private renderButtons(): void {
    if (!this.paypal?.Buttons || this.buttonsRendered) return;

    const container = document.getElementById('paypal-button-container');
    if (!container) return;

    try {
      this.paypal.Buttons({
        style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },

        // The order was already created server-side — hand PayPal its id.
        createOrder: () => Promise.resolve(this.paypalOrderId()),

        onApprove: async () => {
          this.processing.set(true);
          this.errorMessage.set('');
          try {
            const res = await lastValueFrom(
              this.orderService.confirmPaypalPayment(this.orderId!, this.paypalOrderId())
            );
            this.onSuccess.emit({
              orderId: this.orderId,
              isFirstOrder: res?.is_first_order || false,
              dateOfJoining: res?.date_of_joining
            });
          } catch (confirmErr: any) {
            // PayPal answered and refused the capture: nothing was taken and no
            // webhook will complete it. Only hosts listening for failures get told
            // so — for the others the legacy optimistic path below is unchanged.
            if (confirmErr?.error?.declined === true && this.onFailed.observed) {
              this.logger.warn('PayPal capture declined:', confirmErr);
              this.onFailed.emit({ reason: 'declined', message: confirmErr?.error?.error });
              return;
            }
            // Webhook (PAYMENT.CAPTURE.COMPLETED) is the backup — still succeed.
            this.logger.warn('PayPal confirm failed (webhook will finalize):', confirmErr);
            this.onSuccess.emit({ orderId: this.orderId, isFirstOrder: false });
          } finally {
            this.processing.set(false);
          }
        },

        onCancel: () => {
          this.errorMessage.set('');
          this.processing.set(false);
          this.onFailed.emit({ reason: 'cancelled' });
        },

        onError: (err: any) => {
          this.logger.error('PayPal button error:', err);
          this.errorMessage.set('PayPal payment failed. Please try again.');
          this.processing.set(false);
          this.onFailed.emit({ reason: 'error', message: err?.message });
        }
      }).render('#paypal-button-container');

      this.buttonsRendered = true;
    } catch (err) {
      this.logger.error('PayPal render error:', err);
      this.errorMessage.set('Failed to load PayPal buttons. Please refresh.');
    }
  }

  goBack(): void {
    this.onBack.emit();
  }

  async retry(): Promise<void> {
    this.buttonsRendered = false;
    this.errorMessage.set('');
    await this.initializePaypalPayment();
  }
}
